import React, { useEffect, useState, useCallback } from 'react';
import { Snackbar, Alert, Button, Stack, Box, Typography, alpha, useTheme } from '@mui/material';
import { Download, RefreshCw, X } from 'lucide-react';

// Remember a dismissed install prompt for a week so we don't nag on every load.
const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

const recentlyDismissed = () => {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_MS;
};

/**
 * Drives the two PWA touch-points that need UI:
 *   1. Install — captures the browser's `beforeinstallprompt` and offers an
 *      "Install app" action (Chrome/Edge/Android). Hidden once installed.
 *   2. Update — when index.js detects a new service worker, offers "Reload"
 *      to activate it (posts SKIP_WAITING, then reloads on controllerchange).
 */
const PWAInstallPrompt = () => {
  const theme = useTheme();
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [updateReg, setUpdateReg] = useState(null);

  // 1. Installability
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault(); // stop the mini-infobar; we drive our own UI
      if (recentlyDismissed()) return;
      setInstallEvent(e);
      setShowInstall(true);
    };
    const onInstalled = () => {
      setShowInstall(false);
      setInstallEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // 2. Update available
  useEffect(() => {
    const onUpdate = (e) => setUpdateReg(e.detail || null);
    window.addEventListener('pwa:update-available', onUpdate);
    return () => window.removeEventListener('pwa:update-available', onUpdate);
  }, []);

  const install = useCallback(async () => {
    if (!installEvent) return;
    setShowInstall(false);
    installEvent.prompt();
    try { await installEvent.userChoice; } catch (_) { /* ignore */ }
    setInstallEvent(null);
  }, [installEvent]);

  const dismissInstall = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowInstall(false);
  };

  const applyUpdate = useCallback(() => {
    const waiting = updateReg?.waiting;
    if (waiting) {
      // Reload exactly once, when the new worker takes control.
      navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
      waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      window.location.reload();
    }
    setUpdateReg(null);
  }, [updateReg]);

  return (
    <>
      {/* Install banner */}
      <Snackbar
        open={showInstall}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          maxWidth: 380,
          bottom: {
            xs: 'calc(74px + env(safe-area-inset-bottom, 0px)) !important',
            md: '24px !important',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            pl: 2,
            borderRadius: 2.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Box sx={{ width: 38, height: 38, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
            <Download size={20} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.2 }}>Install the admin app</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Add to your device for quick, full-screen access.</Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button size="small" variant="contained" onClick={install} sx={{ fontWeight: 700, px: 1.5 }}>Install</Button>
            <Button size="small" color="inherit" onClick={dismissInstall} sx={{ minWidth: 0, px: 1, color: 'text.disabled' }}><X size={16} /></Button>
          </Stack>
        </Box>
      </Snackbar>

      {/* Update available */}
      <Snackbar
        open={!!updateReg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: {
            xs: 'calc(74px + env(safe-area-inset-bottom, 0px)) !important',
            md: '24px !important',
          },
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={<RefreshCw size={18} />}
          sx={{ alignItems: 'center' }}
          action={<Button color="inherit" size="small" onClick={applyUpdate} sx={{ fontWeight: 800 }}>Reload</Button>}
        >
          A new version is available.
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt;
